import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Brain, Zap, TrendingUp, Users, CheckCircle, ArrowRight, Target } from 'lucide-react'
import Navbar from '../components/layout/Navbar'
import Footer from '../components/layout/Footer'
import { usePageMeta } from '../hooks/usePageMeta'

const beliefs = [
  {
    icon: <Brain className="w-6 h-6" />,
    title: 'Meetings Shape How Companies Operate',
    body: 'Every decision, alignment gap, and missed opportunity traces back to how a team communicates in the room. Meetings are not a side effect of work. They are where work happens, or doesn\'t.',
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: 'AI Belongs in the Room',
    body: 'Keeping discussions on track, managing time, surfacing the right voices, and capturing clear outcomes are things AI is uniquely capable of doing consistently. Rithmo brings that capability into every meeting.',
  },
  {
    icon: <TrendingUp className="w-6 h-6" />,
    title: 'Better Meetings Build Better Organizations',
    body: 'As Rithmo supports more meetings within an organization, it learns how that team operates. Over time, meetings become more focused, more productive, and more effective by default.',
  },
]

const principles = [
  'Structure should serve the team, not burden it',
  'Every voice in the room deserves a fair chance to be heard',
  'Decisions made in meetings should be captured and followed through',
  'Meeting quality compounds, for better or worse, over time',
  'Founders and leaders deserve visibility without being in every room',
  'The best meeting is one that ends with clarity and momentum',
]

function AboutPage() {
  usePageMeta({
    title: 'About Rithmo: Mission and Principles',
    description: 'Rithmo exists to make meetings less painful and more productive. Learn about our mission, core beliefs, and why we built an AI meeting host.',
  })
  return (
    <div className="min-h-screen bg-white">
      <Navbar transparent />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/60 to-white" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow" />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl animate-pulse-slow"
          style={{ animationDelay: '2s' }}
        />

        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="mb-6">
              <span className="badge-primary text-sm">About Rithmo</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Built Around a Simple Belief.{' '}
              <span className="gradient-text">Meetings Should Get Better Over Time.</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Rithmo is an autonomous AI meeting host that helps teams run smarter meetings in
              real time, then learns from every session so the next one is better.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── What We're Building ───────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-xs font-semibold tracking-widest uppercase text-accent-600 mb-4 block">
                What We're Building
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                An AI That Makes Every Meeting{' '}
                <span className="gradient-text">Worth Having</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-lg">
                <p>
                  Meetings drive alignment, decisions, and momentum. Too often, they also create
                  confusion, wasted time, and uneven participation. Rithmo was created to change that.
                </p>
                <p>
                  Rithmo is designed to support structure during the meeting itself, keeping
                  discussions on track, managing time, ensuring balanced participation, and capturing
                  clear outcomes. Then it learns from completed meetings so future ones improve
                  automatically, without any extra work from the team.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="space-y-4"
            >
              {[
                { label: 'Keeps meetings on track in real time', icon: <Target className="w-5 h-5" /> },
                { label: 'Surfaces balanced participation across the team', icon: <Users className="w-5 h-5" /> },
                { label: 'Captures decisions and action items automatically', icon: <CheckCircle className="w-5 h-5" /> },
                { label: 'Learns how each organization runs and improves over time', icon: <Brain className="w-5 h-5" /> },
                { label: 'Gives leaders visibility without requiring their presence in every room', icon: <Zap className="w-5 h-5" /> },
                { label: 'Scales across teams without adding process overhead', icon: <TrendingUp className="w-5 h-5" /> },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center text-white flex-shrink-0 shadow-glow-sm">
                    {item.icon}
                  </div>
                  <span className="text-gray-700 font-medium">{item.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Core Beliefs ──────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              What We <span className="gradient-text">Believe</span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Every decision we make about how Rithmo works traces back to a small set of core
              beliefs about meetings, organizations, and the role AI can play in both.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {beliefs.map((belief, i) => (
              <motion.div
                key={belief.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="card-interactive group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center mb-5 shadow-glow-sm group-hover:shadow-glow transition-shadow duration-300 text-white">
                  {belief.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{belief.title}</h3>
                <p className="text-gray-600 leading-relaxed">{belief.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Dark Vision Section ────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-dark relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-accent-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mx-auto mb-6 shadow-glow">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Our Goal Is Simple.{' '}
              <span className="gradient-text">Run Better Meetings. Build Better Companies.</span>
            </h2>
            <p className="text-lg text-dark-100 leading-relaxed max-w-2xl mx-auto">
              Most meetings are still unmanaged, inconsistent, and inefficient by default. Rithmo
              is being built to change that, bringing intelligent structure, guidance, and
              organizational memory into every conversation, so teams can operate with greater
              clarity, discipline, and momentum.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Principles ────────────────────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              How We Think About{' '}
              <span className="gradient-text">What Good Looks Like</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {principles.map((principle, i) => (
              <motion.div
                key={principle}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="card flex items-start gap-4"
              >
                <CheckCircle className="w-6 h-6 text-accent-500 flex-shrink-0 mt-0.5" />
                <span className="font-medium text-gray-800 leading-snug">{principle}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────── */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative glass rounded-3xl p-12 text-center overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-primary opacity-5" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                See What Rithmo Can Do for Your Team
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
                Join the teams already running smarter meetings with Rithmo.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/signup" className="btn-primary text-lg px-8 py-4">
                  Get Early Access
                </Link>
                <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                  Talk To The Team
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default AboutPage
